import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Alert,
  Card,
  ConfirmModal,
  Icon,
  Label,
  Modal,
  MultiSelect,
  SimpleTooltip,
  SwitchInput,
  TextInput,
  UrlInput,
  Disclosure,
  SimpleSummary,
} from 'corso-admin-ui';
import { CrewMerchantUi, isTruthy, OasWebhookHeader } from 'corso-types';
import { ChangeEventHandler, FormEventHandler, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { LinkProps } from 'react-router-dom';
import Separator from '~/components/Separator';
import SettingsAnchorCard from '~/features/settings/components/SettingsAnchorCard';
import CopyInput from './CopyInput';
import {
  useWebhookConfigData,
  useWebhookCreate,
  useWebhookDelete,
  useWebhookUpdate,
} from './hooks/useWebhooks';

const webhookTopics = Object.values(CrewMerchantUi.ConfigWebhookTopic);

const topicDisplayNames: Record<CrewMerchantUi.ConfigWebhookTopic, string> = {
  Claim_Created: 'Claim Created',
  Claim_Updated: 'Claim Updated',
  Claim_Finalized: 'Claim Finalized',
  Shipping_Claim_Created: 'Shipping Claim Created',
  Shipping_Claim_Updated: 'Shipping Claim Updated',
  Shipping_Claim_Finalized: 'Shipping Claim Finalized',
  Registration_Created: 'Registration Created',
};

const topicDetailedDescriptions: Record<
  CrewMerchantUi.ConfigWebhookTopic,
  string
> = {
  Claim_Created: 'Triggered when a new claim is created',
  Claim_Updated:
    'Triggered when a claim is updated, this can include changes to the claim status, line items, or other metadata.',
  Claim_Finalized: 'Triggered when a claim is finalized.',
  Shipping_Claim_Created:
    'Triggered when a new shipping protection claim is created.',
  Shipping_Claim_Updated:
    'Triggered when a shipping protection claim is updated, this can include changes to the claim status, line items, or other metadata.',
  Shipping_Claim_Finalized:
    'Triggered when a shipping protection claim is finalized.',
  Registration_Created: 'Triggered when a new product registration is created.',
};

function useWebhookCreateForm() {
  return useForm({
    resolver: zodResolver(CrewMerchantUi.configWebhookCreateSchema),
    defaultValues: {
      name: '',
      endpointUrl: '',
      configWebhookEvents: [],
    },
  });
}

function FormActions({
  onCancel,
  onSubmit,
  submitLabel = 'Submit',
  submitVariant = 'primary',
  isDisabled = false,
  isLoading = false,
}: {
  onCancel: () => void;
  onSubmit?: () => void;
  submitLabel?: string;
  submitVariant?: 'primary' | 'destructive';
  isDisabled?: boolean;
  isLoading?: boolean;
}) {
  return (
    <div className="flex flex-row justify-end gap-2">
      <Action onClick={onCancel}>Cancel</Action>
      <Action
        variant={submitVariant}
        type={onSubmit ? 'button' : 'submit'}
        onClick={onSubmit}
        disabled={isDisabled}
        loading={isLoading}
      >
        {submitLabel}
      </Action>
    </div>
  );
}

function WebhookCreateForm({
  showForm,
  closeForm,
}: {
  showForm: boolean;
  closeForm: () => void;
}) {
  const { mutateAsync: createWebhook, isPending } = useWebhookCreate();
  const {
    handleSubmit,
    formState: { errors, isDirty },
    register,
    reset,
    control,
  } = useWebhookCreateForm();

  const closeHandler = () => {
    closeForm();
    reset();
  };

  const submitHandler: FormEventHandler<HTMLFormElement> = (event) => {
    handleSubmit(
      (data) => createWebhook(data).then(closeHandler).catch(console.error),
      console.error,
    )(event).catch(console.error);
  };

  return (
    <Modal
      show={showForm}
      onClose={closeHandler}
      title="Create Webhook"
      description="Configure a webhook to receive notifications when events occur."
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <TextInput
          id="webhook-name"
          label="Name"
          details="A name to identify this webhook."
          required
          {...register('name')}
          error={errors.name?.message}
        />

        <UrlInput
          id="webhook-endpoint-url"
          label="Endpoint URL"
          details="The URL to send webhook events to."
          required
          {...register('endpointUrl')}
          error={errors.endpointUrl?.message}
        />

        <div className="flex flex-col gap-2">
          <Controller
            control={control}
            name="configWebhookEvents"
            render={({ field: { onChange, value }, fieldState }) => {
              const topicOptions = webhookTopics.map((topic) => ({
                label: topicDisplayNames[topic],
                value: topic,
              }));

              return (
                <MultiSelect
                  togglable
                  label="Topics"
                  details="Select the topics to receive webhook events for."
                  options={topicOptions}
                  value={value
                    .map((event) =>
                      topicOptions.find(
                        (option) => option.value === event.topic,
                      ),
                    )
                    .filter(isTruthy)}
                  onChange={(v) =>
                    onChange(
                      v.map((option) => ({
                        topic: option.value,
                        isEnabled: true,
                      })),
                    )
                  }
                  error={fieldState.error?.message}
                />
              );
            }}
          />
        </div>

        <FormActions
          onCancel={closeHandler}
          submitLabel="Create"
          isDisabled={!isDirty || isPending}
          isLoading={isPending}
        />
      </form>
    </Modal>
  );
}

function WebhookEventToggle({
  event,
  configWebhook,
}: {
  event: CrewMerchantUi.ConfigWebhookEvent;
  configWebhook: CrewMerchantUi.ConfigWebhook;
}) {
  const { mutate: updateWebhook, isPending } = useWebhookUpdate();

  const handleToggle: ChangeEventHandler<HTMLInputElement> = (e) => {
    const { checked } = e.target;
    updateWebhook({
      webhookId: configWebhook.id,
      body: {
        configWebhookEvents: configWebhook.configWebhookEvents.map((evt) =>
          evt.id === event.id
            ? { id: evt.id, isEnabled: checked }
            : { id: evt.id, isEnabled: evt.isEnabled },
        ),
      },
    });
  };

  return (
    <div className="flex items-center justify-between border-b py-2 last:border-0">
      <div>
        <span className="text-sm">{topicDisplayNames[event.topic]}</span>
        <p className="text-xs text-neutral-500">
          {topicDetailedDescriptions[event.topic]}
        </p>
      </div>
      <SwitchInput
        id={`webhook-event-toggle-${event.id}`}
        label={`Toggle ${topicDisplayNames[event.topic]}`}
        labelVisuallyHidden
        checked={event.isEnabled}
        onChange={handleToggle}
        disabled={isPending}
      />
    </div>
  );
}

function WebhookHeader({
  name,
  deliveriesTo,
  onDelete,
}: {
  name: string;
  deliveriesTo: LinkProps['to'];
  onDelete: () => void;
}) {
  return (
    <div className="flex flex-1 items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon icon="webhook" className="text-neutral-500" />
        <span className="font-medium">{name}</span>
      </div>
      <div className="flex items-center gap-2">
        <SimpleTooltip content="See deliveries for this webhook">
          <Action
            icon="eye"
            accessibilityLabel={`See deliveries for ${name}`}
            variant="ghost"
            to={deliveriesTo}
          />
        </SimpleTooltip>
        <Action
          icon="remove"
          accessibilityLabel={`Delete ${name}`}
          variant="ghost"
          onClick={onDelete}
        />
      </div>
    </div>
  );
}

function WebhookConfigItem({
  configWebhook,
}: {
  configWebhook: CrewMerchantUi.ConfigWebhook;
}) {
  const { mutate: deleteWebhook, isPending: isDeleting } = useWebhookDelete();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = () => {
    deleteWebhook(configWebhook.id);
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <Disclosure
        renderSummary={
          <SimpleSummary>
            <WebhookHeader
              name={configWebhook.name}
              deliveriesTo={`webhooks/${configWebhook.id}`}
              onDelete={() => setShowDeleteConfirm(true)}
            />
          </SimpleSummary>
        }
      >
        <div className="flex flex-col gap-4 px-6 py-4">
          <CopyInput label="Endpoint URL" value={configWebhook.endpointUrl} />

          <CopyInput
            label="HMAC Secret"
            value={configWebhook.hmacSignatureSecret}
          />

          <CopyInput label="Algorithm" value="SHA-256" />

          <CopyInput label="Encoding" value="Hexadecimal" />

          <Alert
            variant="info"
            heading="Both headers carry the same signature. Use whichever your platform expects. The alternate header is included for compatibility with older systems, which may expect the raw digest."
          />

          <CopyInput
            label="Signature Header"
            value={OasWebhookHeader['X-Corso-Signature']}
          />

          <CopyInput
            label="Alternate Signature Header"
            value={OasWebhookHeader['X-Corso-Signature-SHA-256']}
          />

          <p className="text-xs text-neutral-500">
            Need more help?{' '}
            <Action
              to="https://help.corso.com/integrations/webhooks"
              target="_blank"
              variant="link"
              rel="noreferrer"
            >
              View complete setup guide
            </Action>
          </p>
          <Separator />
          <Label>Events</Label>
          {configWebhook.configWebhookEvents.map((event) => (
            <WebhookEventToggle
              key={event.id}
              event={event}
              configWebhook={configWebhook}
            />
          ))}
        </div>
      </Disclosure>

      <ConfirmModal
        show={showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Webhook"
        prompt="Are you sure you want to delete this webhook? This action cannot be undone."
        confirmText="Delete"
        variant="destructive"
        loading={isDeleting}
      />
    </>
  );
}

export default function WebhookSettings() {
  const { data: webhooks = [], isLoading } = useWebhookConfigData();
  const [showForm, setShowForm] = useState(false);

  return (
    <SettingsAnchorCard id="webhooks">
      <Card.Line as="header">
        <Card.Heading as="h3">Webhook Configurations</Card.Heading>
        <Action
          icon="add"
          onClick={() => setShowForm(true)}
          variant="ghost"
          accessibilityLabel="Add Webhook"
        />
      </Card.Line>
      <Card.Description>
        Configure webhooks to receive notifications when events occur in Corso.
        You can use webhooks to integrate with other systems or third-party
        services.
      </Card.Description>

      {/* oxlint-disable-next-line no-nested-ternary */}
      {isLoading ? (
        <p className="text-sm text-neutral-400 italic">Loading...</p>
      ) : webhooks.length > 0 ? (
        <div className="flex flex-col gap-2">
          {webhooks.map((webhook) => (
            <WebhookConfigItem key={webhook.id} configWebhook={webhook} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-neutral-400 italic">
          No webhooks configured yet.
        </p>
      )}

      <WebhookCreateForm
        showForm={showForm}
        closeForm={() => setShowForm(false)}
      />
    </SettingsAnchorCard>
  );
}
